import { useState } from "react";
import { History } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";

interface AuditRow {
  id: string;
  action: string;
  action_display: string;
  actor: string | null;
  actor_name: string;
  target: string | null;
  target_name: string;
  detail: Record<string, unknown>;
  created_at: string;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

const ACTION_FILTERS = [
  { value: "",             label: "All actions" },
  { value: "create",       label: "Created user" },
  { value: "edit",         label: "Edited user" },
  { value: "deactivate",   label: "Deactivated" },
  { value: "reactivate",   label: "Reactivated" },
  { value: "role_change",  label: "Role change" },
  { value: "invite_sent",  label: "Invite sent" },
  { value: "bulk_import",  label: "Bulk import" },
];

/** /audit-log — admin-only audit trail of user-management actions. */
export default function AuditLog() {
  const [action, setAction] = useState("");
  const [page,   setPage]   = useState(1);

  const { data, isLoading } = useQuery<Paginated<AuditRow>>({
    queryKey: ["user-actions", action, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (action) params.set("action", action);
      if (page)   params.set("page",   String(page));
      const { data } = await api.get<Paginated<AuditRow>>(
        `/user-actions/?${params.toString()}`,
      );
      return data;
    },
  });

  const rows = data?.results ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header>
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
          <History className="h-3.5 w-3.5" />Audit log
        </div>
        <h1 className="mt-1 text-3xl font-bold tracking-tight">User-management activity</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every create / edit / role change / invite / bulk import on a user account, in chronological order.
        </p>
      </header>

      <Card className="border-border/60 shadow-soft">
        <CardContent className="p-0">
          {/* Filter bar */}
          <div className="flex items-center gap-3 border-b border-border/60 p-4">
            <select
              value={action}
              onChange={(e) => { setAction(e.target.value); setPage(1); }}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium"
            >
              {ACTION_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <Button asChild variant="outline" size="sm" className="ml-auto">
              <a href={`/api/v1/user-actions/?format=csv${action ? `&action=${action}` : ""}`} download>
                Export CSV
              </a>
            </Button>
            <span className="text-xs text-muted-foreground">{data?.count ?? 0} entries</span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Target</TableHead>
                <TableHead className="hidden lg:table-cell">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5} className="py-3">
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                    Nothing matches.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} className="border-border/60">
                    <TableCell className="pl-5 py-3 text-xs text-muted-foreground">
                      {formatDateTime(r.created_at)}
                    </TableCell>
                    <TableCell className="py-3">
                      <ActionBadge action={r.action} label={r.action_display} />
                    </TableCell>
                    <TableCell className="py-3 text-sm">{r.actor_name}</TableCell>
                    <TableCell className="py-3 text-sm">{r.target_name}</TableCell>
                    <TableCell className="hidden lg:table-cell py-3 text-xs text-muted-foreground">
                      <Details detail={r.detail} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {(data?.count ?? 0) > 20 && (
            <div className="flex items-center justify-between gap-4 border-t border-border/60 p-3">
              <span className="text-xs text-muted-foreground">
                Page {page} / {Math.max(1, Math.ceil((data?.count ?? 0) / 20))}
              </span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" disabled={!data?.previous}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
                <Button variant="ghost" size="sm" disabled={!data?.next}
                  onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ActionBadge({ action, label }: { action: string; label: string }) {
  const cls =
    action === "create"     ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30"
  : action === "deactivate" ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30"
  : action === "reactivate" ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30"
  : action === "role_change"? "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-500/30"
  : action === "invite_sent"? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30"
                            : "bg-muted/40 text-foreground/80 border-border";
  return <Badge variant="outline" className={cn("border font-medium", cls)}>{label}</Badge>;
}

function Details({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail || {});
  if (!entries.length) return <span>—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.slice(0, 4).map(([k, v]) => (
        <span key={k} className="inline-flex items-center gap-1 rounded-full border bg-muted/30 px-1.5 py-px font-mono text-[10px]">
          <span className="text-muted-foreground/80">{k}:</span>
          <span className="max-w-[12rem] truncate">{String(v)}</span>
        </span>
      ))}
      {entries.length > 4 && <span className="text-[10px]">+{entries.length - 4}</span>}
    </div>
  );
}
